import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { freight } from "../../lib/freightApi.js";
import { parseTranscript } from "../fromApi.js";
import { transferLabel, handoffLabel } from "../recruitingView.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Button, Quiet, Note, C, when, errText } from "./ui.jsx";
import { standardsRows, standardsLine, RESULT, SOURCE, actionsFor, ACTION, STATUS_LINE, callWords } from "./hiringView.js";

/**
 * One applicant (the schematic's "Hiring · one applicant" board): how they line up with the
 * carrier's standards, the call, and the recruiter's next step. No overall score, on purpose: the
 * recruiter decides (Brian, 2026-10-29).
 */

const cell = { padding: "14px 16px 14px 0", borderBottom: `1px solid ${C.soft}`, verticalAlign: "top", textAlign: "left" };
const head = { textAlign: "left", fontSize: 14, fontWeight: 600, color: C.muted, padding: "0 16px 10px 0", borderBottom: `1px solid ${C.line}` };

export function Pill({ result }) {
  const r = RESULT[result] || RESULT.check;
  return <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 999, background: r.bg, color: r.fg, fontSize: 15, fontWeight: 600, whiteSpace: "nowrap" }}>{r.label}</span>;
}

export function Transcript({ text }) {
  const turns = parseTranscript(text);
  if (!turns.length) return <Quiet>Nothing was written down for this call.</Quiet>;
  return (
    <ol aria-label="What was said" style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
      {turns.map((t, i) => (
        <li key={i} style={{ display: "flex", gap: 12, fontSize: 16, lineHeight: 1.5 }}>
          <span style={{ minWidth: 84, fontWeight: 600, color: t.who === "alfred" ? C.accent : C.ink }}>{t.who === "alfred" ? "Alfred" : t.who ? (t.speaker || "Them") : ""}</span>
          <span style={{ flex: 1, minWidth: 0 }}>{t.text}</span>
        </li>
      ))}
    </ol>
  );
}

export function ApplicantView({ company, stage, data, error, busy, msg, open, setOpen, onAct, onTick }) {
  if (error) return <Shell company={company} stage={stage}><Back to="/carrier/hiring">Hiring</Back><Quiet>{error}</Quiet></Shell>;
  if (!data) return <Shell company={company} stage={stage}><Back to="/carrier/hiring">Hiring</Back><Quiet>Loading…</Quiet></Shell>;
  const { applicant: a, criteria, calls = [], onboardingItems = [] } = data;
  const first = String(a.name || "").split(/\s+/)[0] || "They";
  const call = calls[0] || null;
  // Home time is asked on the call: only a call a person answered has asked it.
  const rows = standardsRows(a, criteria, { called: calls.some((c) => c.status === "completed" && !c.outcome) });
  const applied = a.appliedAt || a.createdAt;
  const sub = [`Applied through ${SOURCE[a.source] || "an import"}${applied ? ` on ${new Date(applied).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}.`,
    call && call.status !== "failed" ? `Alfred called ${when(call.startedAt || call.createdAt)}.` : null].filter(Boolean).join(" ");
  const actions = actionsFor(a.status);
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/hiring">Hiring</Back>
      <Title sub={sub}>{a.name}</Title>

      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Your standards</h2>
        <p style={{ margin: 0, fontSize: 17 }}>{standardsLine(rows)}</p>
        {/* On a phone the table becomes one block per standard (review: the Result column fell off-screen). */}
        <dl className="cp-narrow" style={{ margin: 0 }}>
          {rows.map((r) => (
            <div key={r.key} style={{ padding: "12px 0", borderBottom: `1px solid ${C.soft}`, display: "flex", flexDirection: "column", gap: 4 }}>
              <dt style={{ fontWeight: 600, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>{r.label} <Pill result={r.result} /></dt>
              <dd style={{ margin: 0, color: C.muted }}>{`You require: ${r.require}`}</dd>
              <dd style={{ margin: 0 }}>{`${first}'s answer: ${r.said}`}</dd>
            </div>
          ))}
        </dl>
        <div className="cp-wide" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", minWidth: 600, borderCollapse: "collapse", fontSize: 16 }}>
            <thead>
              <tr>
                <th scope="col" style={{ ...head, width: "22%" }}>Standard</th>
                <th scope="col" style={{ ...head, width: "32%" }}>You require</th>
                <th scope="col" style={{ ...head, width: "28%" }}>{`${first}'s answer`}</th>
                <th scope="col" style={{ ...head, width: "18%", textAlign: "right", paddingRight: 0 }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key}>
                  <th scope="row" style={{ ...cell, fontWeight: 600, borderBottom: i === rows.length - 1 ? "none" : cell.borderBottom }}>{r.label}</th>
                  <td style={{ ...cell, color: r.require === "Not on file" ? C.muted : C.ink, borderBottom: i === rows.length - 1 ? "none" : cell.borderBottom }}>{r.require}</td>
                  <td style={{ ...cell, borderBottom: i === rows.length - 1 ? "none" : cell.borderBottom }}>{r.said}</td>
                  <td style={{ ...cell, padding: "12px 0", textAlign: "right", borderBottom: i === rows.length - 1 ? "none" : cell.borderBottom }}><Pill result={r.result} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ margin: 0, fontSize: 14, color: C.muted }}>From the application and the call. Alfred never asks about driving records or drug tests; your recruiter covers those.</p>
      </Card>

      <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>The call</h2>
        <p style={{ margin: 0, fontSize: 16 }}>{callWords(call, when)}</p>
        {call && [transferLabel(call.transfer), handoffLabel(call)].filter(Boolean).map((t) => <p key={t} style={{ margin: 0, fontSize: 15, color: C.muted }}>{t}</p>)}
        {call?.transcript && (
          <>
            <div><Button kind="quiet" onClick={() => setOpen(!open)} style={{ padding: 0 }} expanded={!!open}>{open ? "Hide what was said" : "Read what was said"}</Button></div>
            {open && <Transcript text={call.transcript} />}
          </>
        )}
        {calls.length > 1 && <p style={{ margin: 0, fontSize: 14, color: C.muted }}>{`Alfred has called ${first} ${calls.length} times. This is the latest.`}</p>}
      </Card>

      {a.status === "hired" && onboardingItems.length > 0 && (
        <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Getting them on the road</h2>
          {onboardingItems.map((item) => {
            const done = a.onboarding?.[item.key]?.done === true;
            return (
              <label key={item.key} htmlFor={`ob-${item.key}`} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 16, minHeight: 36 }}>
                <input id={`ob-${item.key}`} type="checkbox" checked={done} disabled={busy} onChange={(e) => onTick(item.key, e.target.checked)} style={{ width: 20, height: 20 }} />
                {item.label}
              </label>
            );
          })}
        </Card>
      )}

      {STATUS_LINE[a.status] && <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{STATUS_LINE[a.status]}</p>}
      {actions.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {actions.map((k, i) => (
            <Button key={k} kind={i === 0 ? "primary" : "secondary"} disabled={busy} onClick={() => onAct(k)} style={{ minHeight: 48, fontSize: 16, padding: "0 22px" }}>{ACTION[k]}</Button>
          ))}
        </div>
      )}
      <Note msg={msg} />
    </Shell>
  );
}

export default function Applicant() {
  const { carrier } = useCarrier();
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    freight.applicant(id).then((d) => alive && setData(d)).catch((e) => alive && setError(e?.status === 404 ? "No such applicant." : errText(e)));
    return () => { alive = false; };
  }, [id]);

  const onAct = async (k) => {
    setBusy(true); setMsg(null);
    try {
      if (k === "hire") {
        const r = await freight.hireApplicant(data.applicant.id);
        setData((d) => ({ ...d, applicant: r.applicant }));
        setMsg({ ok: true, text: `${data.applicant.name} is on your roster. Alfred calls them about loads once they agree to it.` });
      } else {
        await freight.updateApplicant(data.applicant.id, { status: k });
        nav("/carrier/hiring", { state: { note: `${data.applicant.name}: ${k === "forward" ? "moving forward" : "not a fit"}.` } });
      }
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const onTick = async (item, done) => {
    setBusy(true); setMsg(null);
    try { const a = await freight.setOnboarding(data.applicant.id, item, done); setData((d) => ({ ...d, applicant: a })); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <ApplicantView company={carrier.name} stage={carrier.stage} data={data} error={error} busy={busy} msg={msg} open={open} setOpen={setOpen} onAct={onAct} onTick={onTick} />;
}
